import React, { useEffect, useMemo, useState } from 'react';
import {
  getSelaProjectEvidence,
  type SelaChatCitation,
  type SelaGraphNode,
  type SelaSourceRef,
} from '../../../services/selaApi';

type GraphEvidenceFact = {
  citation: SelaChatCitation;
  node: SelaGraphNode | null;
  sources: SelaSourceRef[];
};

export function citationAnchorId(messageId: string, citationId: number): string {
  return `citation-${messageId.replace(/[^a-zA-Z0-9_-]/g, '-')}-${citationId}`;
}

export function normalizeDisplayCitations(citations: SelaChatCitation[]): {
  citations: SelaChatCitation[];
  markerMap: Map<number, number[]>;
} {
  const markerMap = new Map<number, number[]>();
  const displayCitations: SelaChatCitation[] = [];
  let nextId = 1;

  [...citations]
    .sort((a, b) => a.id - b.id)
    .forEach((citation) => {
      const nestedSourceCitations = citation.type !== 'graph_fact' && Array.isArray(citation.citations)
        ? citation.citations.filter((item) => item && (item.source || item.source_link || item.chunk || item.text))
        : [];

      if (nestedSourceCitations.length > 0) {
        const displayIds: number[] = [];
        nestedSourceCitations.forEach((nested) => {
          const displayId = nextId;
          nextId += 1;
          displayIds.push(displayId);
          displayCitations.push({
            ...citation,
            ...nested,
            id: displayId,
            type: citation.type || 'rag',
            answer: citation.answer,
            query: citation.query,
            citations: undefined,
          });
        });
        markerMap.set(citation.id, displayIds);
        return;
      }

      const displayId = nextId;
      nextId += 1;
      markerMap.set(citation.id, [displayId]);
      displayCitations.push({ ...citation, id: displayId });
    });

  return { citations: displayCitations, markerMap };
}

export function linkCitationMarkers(
  content: string,
  citations: SelaChatCitation[],
  messageId: string,
  markerMap: Map<number, number[]>,
): string {
  if (!content || citations.length === 0) return content;
  const validIds = new Set(citations.map((citation) => citation.id));
  const renderMarkers = (rawId: string, fallback: string) => {
    const ids = markerMap.get(Number(rawId)) || (validIds.has(Number(rawId)) ? [Number(rawId)] : []);
    if (!ids.length) return fallback;
    return ids.map((id) => `[[${id}]](#${citationAnchorId(messageId, id)})`).join('');
  };
  return content
    .replace(/\[(?:Citation|citation)\s+(\d+)\](?!\()/g, (match, rawId) => renderMarkers(rawId, match))
    .replace(/\[(\d+)\](?!\()/g, (match, rawId) => renderMarkers(rawId, match));
}

function sourceLabel(source: SelaSourceRef): string {
  return [
    source.sheetNumber ? `Sheet ${source.sheetNumber}` : '',
    typeof source.pageNumber === 'number' ? `p.${source.pageNumber}` : '',
  ].filter(Boolean).join(' · ') || source.documentName || 'Source';
}

function stringProperty(properties: Record<string, unknown> | undefined, key: string): string {
  const value = properties?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

function isEcaEvidence(item: GraphEvidenceFact): boolean {
  const type = stringProperty(item.node?.properties, 'type').toUpperCase();
  return type === 'ECA' || item.node?.label === 'ECA Development Risk' || item.citation.fact?.label === 'ECA Development Risk';
}

/** Stable key so evidence loads once per message, not on every citations[] re-render. */
function uniqueGraphCitations(citations: SelaChatCitation[]): SelaChatCitation[] {
  const seen = new Set<string>();
  const graphCitations: SelaChatCitation[] = [];
  for (const citation of citations) {
    if (citation.type !== 'graph_fact' || !citation.nodeId || seen.has(citation.nodeId)) {
      continue;
    }
    seen.add(citation.nodeId);
    graphCitations.push(citation);
  }
  return graphCitations;
}

/** Stable key so evidence loads once per message, not on every citations[] re-render. */
function graphCitationNodeKey(citations: SelaChatCitation[]): string {
  return uniqueGraphCitations(citations)
    .map((citation) => citation.nodeId || '')
    .sort()
    .join('|');
}

const evidenceRequestCache = new Map<
  string,
  ReturnType<typeof getSelaProjectEvidence>
>();

function fetchProjectEvidenceCached(projectId: string, nodeId: string) {
  const cacheKey = `${projectId}:${nodeId}`;
  let pending = evidenceRequestCache.get(cacheKey);
  if (!pending) {
    pending = getSelaProjectEvidence(projectId, nodeId);
    evidenceRequestCache.set(cacheKey, pending);
  }
  return pending;
}

export function evidenceForRefs(refs: Array<number | string> | undefined, citations: SelaChatCitation[]): {
  citations: SelaChatCitation[];
  textEvidence: string[];
} {
  const numericRefs = new Set<number>();
  const textEvidence: string[] = [];
  const matchedCitationIds = new Set<number>();
  const normalizeEvidenceText = (value: unknown) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const citationSearchText = (citation: SelaChatCitation) => normalizeEvidenceText([
    citation.source,
    citation.source_link,
    citation.page,
    citation.chunk,
    citation.text,
    citation.answer,
    citation.query,
    citation.fact?.label,
    citation.fact?.value,
    citation.fact?.unit,
    citation.fact?.category,
    ...(citation.sources || []).flatMap((source) => [
      source.documentName,
      source.sheetNumber,
      source.sheetName,
      source.quote,
      source.sourceUrl,
      source.mapUrl,
    ]),
    ...(citation.citations || []).flatMap((source) => [
      source.source,
      source.source_link,
      source.page,
      source.chunk,
      source.text,
    ]),
  ].filter(Boolean).join(' '));

  for (const ref of refs || []) {
    const text = String(ref || '').trim();
    if (!text) continue;
    const numeric = Number(text);
    if (Number.isFinite(numeric)) {
      numericRefs.add(numeric);
    } else {
      const normalizedRef = normalizeEvidenceText(text);
      const matched = (citations || []).find((citation) => {
        const haystack = citationSearchText(citation);
        return haystack.includes(normalizedRef)
          || normalizedRef.includes(haystack)
          || normalizedRef.split(' ').filter((word) => word.length > 3).some((word) => haystack.includes(word));
      });
      if (matched) {
        matchedCitationIds.add(Number(matched.id));
      } else {
        textEvidence.push(text);
      }
    }
  }
  const allCitationIds = new Set<number>();
  numericRefs.forEach((id) => allCitationIds.add(id));
  matchedCitationIds.forEach((id) => allCitationIds.add(id));
  return {
    citations: allCitationIds.size
      ? (citations || []).filter((citation) => allCitationIds.has(Number(citation.id)))
      : [],
    textEvidence,
  };
}

export function EvidenceStrip({
  projectId,
  citations,
  messageId,
  title = 'Evidence and sources',
  note = 'The answer cites these items using the same bracketed numbers.',
  compact = false,
  anchorCitations = true,
  textEvidence = [],
  defaultExpanded = false,
}: {
  projectId: string;
  citations: SelaChatCitation[];
  messageId: string;
  title?: string;
  note?: string;
  compact?: boolean;
  anchorCitations?: boolean;
  textEvidence?: string[];
  defaultExpanded?: boolean;
}) {
  const [graphItems, setGraphItems] = useState<GraphEvidenceFact[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { citations: displayCitations } = useMemo(() => normalizeDisplayCitations(citations), [citations]);
  const graphCitationKey = graphCitationNodeKey(displayCitations);
  const orderedCitations = useMemo(() => {
    const seen = new Set<number>();
    return [...displayCitations]
      .filter((citation) => {
        if (seen.has(citation.id)) return false;
        seen.add(citation.id);
        return true;
      })
      .sort((a, b) => a.id - b.id);
  }, [displayCitations]);

  useEffect(() => {
    const graphCitations = uniqueGraphCitations(displayCitations);
    if (!projectId || !graphCitationKey) {
      setGraphItems([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    async function loadGraphEvidence() {
      try {
        const resolved = await Promise.all(
          graphCitations.map(async (citation) => {
            const nodeId = citation.nodeId || '';
            try {
              const detail = await fetchProjectEvidenceCached(projectId, nodeId);
              return {
                citation,
                node: detail.node,
                sources: detail.sources.length ? detail.sources : citation.sources || [],
              };
            } catch {
              return { citation, node: null, sources: citation.sources || [] };
            }
          }),
        );

        if (!cancelled) {
          setGraphItems(resolved.filter((item) => item.sources.length > 0));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadGraphEvidence();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by node ids, not citations array identity
  }, [graphCitationKey, projectId]);

  const cleanTextEvidence = textEvidence.filter((item) => item.trim());

  if (loading && orderedCitations.length === 0 && cleanTextEvidence.length === 0) {
    return (
      <div className="mt-4 rounded-2xl border border-[#dbe4f0] bg-[#f8fbff] px-4 py-3 text-xs text-[#647488]">
        Checking evidence sources...
      </div>
    );
  }
  if (orderedCitations.length === 0 && cleanTextEvidence.length === 0) return null;
  const evidenceCount = orderedCitations.length + cleanTextEvidence.length;

  return (
    <section className={`${compact ? 'mt-3' : 'mt-5'} overflow-hidden rounded-2xl border border-[#d6e2f2] bg-[#f8fbff] shadow-[0_18px_45px_rgba(30,68,116,0.08)]`}>
      <button
        type="button"
        onClick={() => setExpanded((current) => !current)}
        className={`flex w-full items-center justify-between gap-3 border-b border-[#dbe4f0] bg-white/70 text-left hover:bg-white ${compact ? 'px-3 py-2' : 'px-4 py-3'}`}
        aria-expanded={expanded}
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0f172a]">
            {title}
          </span>
          <span className="text-xs text-[#647488]">
            {note}
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-[#647488]">
            {evidenceCount} {evidenceCount === 1 ? 'item' : 'items'}
          </span>
        </div>
        <span className={`shrink-0 text-[#858a9a] transition-transform ${expanded ? 'rotate-180' : ''}`}>
          ▾
        </span>
      </button>
      {expanded ? (
        <div className={`grid gap-3 p-3 ${compact ? 'lg:grid-cols-1' : 'lg:grid-cols-2'}`}>
          {cleanTextEvidence.map((item) => (
            <article
              key={item}
              className="rounded-xl border border-[#e1e8f0] bg-white p-3 shadow-sm"
            >
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[#647488]">
                Evidence reference
              </div>
              <p className="text-xs font-semibold leading-relaxed text-[#334155]">{item}</p>
            </article>
          ))}
          {orderedCitations.map((citation) => {
            const graphItem = citation.type === 'graph_fact'
              ? graphItems.find((item) => item.citation.id === citation.id || item.citation.nodeId === citation.nodeId)
              : undefined;
            const graphSources = graphItem?.sources.length ? graphItem.sources : citation.sources || [];
            const source = graphSources[0];
            const fact = citation.fact || {};
            const mapUrl = graphItem && isEcaEvidence(graphItem)
              ? graphSources.find((src) => src.mapUrl)?.mapUrl || ''
              : '';
            const nestedSourceCitations = Array.isArray(citation.citations)
              ? citation.citations.filter((item) => item && (item.source || item.source_link || item.chunk || item.text))
              : [];
            const firstNestedSource = nestedSourceCitations[0];
            const sourceLabelText = citation.source
              || firstNestedSource?.source
              || citation.source_link
              || firstNestedSource?.source_link
              || `Source ${citation.id}`;
            const sourceText = citation.text || citation.chunk || citation.answer || '';

            if (citation.type !== 'graph_fact') {
              return (
                <article
                  key={`${citation.id}-${sourceLabelText}`}
                  id={anchorCitations ? citationAnchorId(messageId, citation.id) : undefined}
                  className={`${anchorCitations ? 'scroll-mt-24 target:border-amber-300 target:bg-amber-50/70' : ''} rounded-xl border border-[#e1e8f0] bg-white ${compact ? 'p-3' : 'p-4'} shadow-sm transition`}
                >
                  <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[#647488]">
                    Source [{citation.id}]
                  </div>
                  {citation.source_link ? (
                    <a
                      href={citation.source_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold text-[#3251b7] underline hover:text-[#1e4474]"
                    >
                      {sourceLabelText}
                    </a>
                  ) : (
                    <h4 className="text-sm font-bold text-[#0f172a]">{sourceLabelText}</h4>
                  )}
                  {citation.page ? (
                    <p className="mt-1 text-[11px] font-medium text-[#647488]">Page {citation.page}</p>
                  ) : null}
                  {nestedSourceCitations.length ? (
                    <div className="mt-3 space-y-3">
                      {nestedSourceCitations.map((nested, index) => {
                        const nestedLabel = nested.source || nested.source_link || `PDF source ${index + 1}`;
                        const nestedText = nested.text || nested.chunk || '';
                        return (
                          <div key={`${nestedLabel}-${index}`} className="rounded-lg border border-[#e7e9ee] bg-[#fbfdff] p-3">
                            {nested.source_link ? (
                              <a
                                href={nested.source_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-bold text-[#3251b7] underline hover:text-[#1e4474]"
                              >
                                {nestedLabel}
                              </a>
                            ) : (
                              <p className="text-xs font-bold text-[#0f172a]">{nestedLabel}</p>
                            )}
                            {nested.page ? (
                              <p className="mt-1 text-[11px] font-medium text-[#647488]">Page {nested.page}</p>
                            ) : null}
                            {nestedText ? (
                              <blockquote className="mt-2 border-l-2 border-[#d6e2f2] pl-3 text-xs italic leading-relaxed text-[#475669]">
                                "{nestedText.length > 260 ? `${nestedText.slice(0, 260)}...` : nestedText}"
                              </blockquote>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  {sourceText ? (
                    <blockquote className="mt-3 border-l-2 border-[#d6e2f2] pl-3 text-xs italic leading-relaxed text-[#475669]">
                      "{sourceText.length > 220 ? `${sourceText.slice(0, 220)}...` : sourceText}"
                    </blockquote>
                  ) : null}
                </article>
              );
            }

            return (
              <article
                key={citation.nodeId || citation.id}
                id={anchorCitations ? citationAnchorId(messageId, citation.id) : undefined}
                className={`group ${anchorCitations ? 'scroll-mt-24 target:border-amber-300 target:bg-amber-50/70' : ''} overflow-hidden rounded-xl border border-[#e1e8f0] bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-[#3251b7]/50 hover:shadow-md`}
              >
                <div className={`grid ${compact ? 'grid-cols-[82px_1fr]' : 'grid-cols-[112px_1fr]'} gap-3 p-3`}>
                  {source?.pageImageUrl ? (
                    <a
                      href={source.pageImageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block overflow-hidden rounded-lg border border-[#e7e9ee] bg-[#f7fafd]"
                    >
                      <img
                        src={source.pageImageUrl}
                        alt={sourceLabel(source)}
                        className={`${compact ? 'h-20' : 'h-28'} w-full object-cover transition group-hover:scale-105`}
                        loading="lazy"
                      />
                    </a>
                  ) : (
                    <div className={`flex ${compact ? 'h-20' : 'h-28'} items-center justify-center rounded-lg border border-dashed border-[#cdd5e1] bg-[#f7fafd] text-[10px] text-[#94a3b8]`}>
                      No preview
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-semibold uppercase tracking-wide text-[#647488]">
                      <span>[{citation.id}]</span>
                      <span>{fact.category || 'fact'}</span>
                      {typeof fact.confidence === 'number' ? (
                        <span>
                          {(fact.confidence * 100).toFixed(0)}%
                        </span>
                      ) : null}
                    </div>
                    <h4 className="mt-2 truncate text-sm font-bold text-[#0f172a]">{fact.label || citation.source}</h4>
                    <p className="mt-0.5 line-clamp-2 text-sm text-[#334155]">
                      {fact.value || citation.text || 'No value captured'}{fact.unit ? ` ${fact.unit}` : ''}
                    </p>
                    {source ? (
                      <p className="mt-2 text-[11px] font-medium text-[#647488]">
                        {sourceLabel(source)}
                        {source.documentName ? ` · ${source.documentName}` : ''}
                      </p>
                    ) : null}
                    {mapUrl ? (
                      <a
                        href={mapUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] font-semibold text-amber-800 hover:border-amber-400 hover:bg-amber-100"
                      >
                        View ECA layer map
                      </a>
                    ) : null}
                  </div>
                </div>
                {source?.quote ? (
                  <blockquote className="border-t border-[#eef1f6] bg-[#fbfdff] px-3 py-2 text-xs italic leading-relaxed text-[#475669]">
                    "{source.quote.length > 150 ? `${source.quote.slice(0, 150)}...` : source.quote}"
                  </blockquote>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
